import { useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { useAppStore, useSelectedProductionLine } from '@stores/appStore';
import { apiClient } from '@lib/api';
import { cn } from '@lib/utils';
import { useTenant } from '@providers/TenantProvider';

export function ProductionLineSelector() {
  const selectedLine = useSelectedProductionLine();
  const { config } = useTenant();
  const {
    availableProductionLines,
    setAvailableProductionLines,
    setSelectedProductionLine,
  } = useAppStore();

  // Fetch available production lines once on mount.
  // We intentionally do NOT include selectedLine in deps — doing so would
  // re-fetch on every selection change and create stale-closure race conditions
  // where an in-flight callback captures the old selectedLine value.
  // Instead we read the current value from the store directly inside the callback.
  useEffect(() => {
    const controller = new AbortController();

    async function fetchProductionLines() {
      try {
        const response = await apiClient.get('/production-lines', {
          signal: controller.signal,
        });
        const lines = response.data.map((line: { name: string; label: string; lineType: string; timeZone: string; bottleneckObjects?: string[] }) => ({
          id: line.name,
          label: line.label,
          lineType: line.lineType as 'beverages' | 'pc' | 'tc',
          timeZone: line.timeZone,
          bottleneckObjects: line.bottleneckObjects ?? [],
        }));
        setAvailableProductionLines(lines);

        // Read the live store value — not a stale closure — to decide whether
        // to auto-select. This prevents a resolved fetch from overriding a
        // production line the user already selected.
        // Filter lines to only visible ones (per tenant config), or all beverages lines
        const visibleLineIds = config.visibleLines;
        const allowedLines = (visibleLineIds && visibleLineIds.length > 0)
          ? lines.filter((l: { id: string }) => visibleLineIds.includes(l.id))
          : lines.filter((l: { lineType: string }) => l.lineType === 'beverages');

        const currentLine = useAppStore.getState().selectedProductionLine;
        // If current selection is not in the allowed lines, reset it
        const currentIsAllowed = currentLine && allowedLines.some((l: { id: string }) => l.id === currentLine.id);
        if ((!currentLine || !currentIsAllowed) && allowedLines.length > 0) {
          setSelectedProductionLine(allowedLines[0]);
        } else if (currentLine && currentIsAllowed) {
          // Refresh the selected line with full API data (e.g. bottleneckObjects)
          // since the cookie may hold a stale version without newer fields.
          const freshLine = lines.find((l: { id: string }) => l.id === currentLine.id);
          if (freshLine) {
            setSelectedProductionLine(freshLine);
          }
        }
      } catch (error: unknown) {
        if ((error as { code?: string })?.code !== 'ERR_CANCELED') {
          console.error('Failed to fetch production lines:', error);
        }
      }
    }

    fetchProductionLines();

    return () => controller.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setAvailableProductionLines, setSelectedProductionLine]);

  const beveragesLines = availableProductionLines.filter((l) => {
    if (config.visibleLines && config.visibleLines.length > 0) {
      return config.visibleLines.includes(l.id);
    }
    return l.lineType === 'beverages';
  });

  if (beveragesLines.length === 0) {
    return null;
  }

  return (
    <div className="relative">
      <select
        value={selectedLine?.id || ''}
        onChange={(e) => {
          const line = availableProductionLines.find((l) => l.id === e.target.value);
          if (line) {
            setSelectedProductionLine(line);
          }
        }}
        className={cn(
          'h-9 rounded-md border border-input bg-background px-3 pr-8 text-sm',
          'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
          'appearance-none cursor-pointer'
        )}
      >
        {beveragesLines.map((line) => (
          <option key={line.id} value={line.id}>
            {line.label}
          </option>
        ))}
      </select>
      <ChevronDown className="absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 opacity-50 pointer-events-none" />
    </div>
  );
}

export default ProductionLineSelector;
